import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import useSpeechEngine from "shared/hooks/useSpeechEngine";
import { appService, languageService, speechTokenCacheService, translationService } from 'services';
import useAudioPlayer from 'shared/hooks/useAudioPlayer';
import { downloadFile } from 'shared/helper/common-helper';
import { useAppSettings } from 'shared/context/AppSettingsContext';
import TranscriptControl from 'components/TranscriptControl';
import TranscriptionDisplay from 'components/TranscriptionDisplay';
import ModalPopup from 'components/ModalPopup';
import { ListeningStatus, SliceOfConversationToPassToAPI } from 'shared/config/constants';
import { MicrophoneBlockedError, SpeechTokenError, TranslationError } from 'shared/errors';
import styles from './styles.module.scss';
import { handleError } from 'shared/errors/handleError';
import Feedback from 'components/Feedback';
import { appInsights } from 'services/app-insight/app-insight';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useSession } from 'shared/hooks/useSession';
import  useWindowClose  from 'shared/hooks/useWindowClose';
import { IoIosInformationCircle } from 'react-icons/io';
import CountdownModal from 'components/TimeCountdown'
import useListeningWatchdog  from 'shared/hooks/useListeningWatch';

const StopReason = Object.freeze({
    NONE: 'NONE',
    CONVERSATION_ENDED: 'CONVERSATION_ENDED',
    NEW_CONVERSATION: 'NEW_CONVERSATION',
});

const LanguageTranslator = () => {
    const appSettings = useAppSettings();
    const { startSessionConversation, endSessionConversation, endSessionConversationBeacon  } = useSession();
    const [continuousMode, setContinuousMode] = useState(false);
    const conversationId = useMemo(() => appSettings.settings.conversationId);
    const sourceLanguage = useMemo(() => languageService.getLocale(appSettings.settings.sourceLanguage, appSettings.settings.sourceDialect), [appSettings.settings.sourceLanguage, appSettings.settings.sourceDialect]);
    const targetLanguage = useMemo(() => languageService.getLocale(appSettings.settings.targetLanguage, appSettings.settings.targetDialect), [appSettings.settings.targetLanguage, appSettings.settings.targetDialect]);
    const [conversation, setConversation] = useState([]);
    const [listeningStatus, setListeningStatus] = useState(ListeningStatus.Preparing);
    const stopStateRef = useRef({
        isStopped: false,
        reason: StopReason.NONE
    });
    const [currentMessage, setCurrentMessage] = useState({
        language: "",
        text: "",
    });
    const [showResetPopup, setShowResetPopup] = useState(false);
    const [showLanguageChangePopup, setShowLanguageChangePopup] = useState(false);
    const [showLanguageChangeToHaitianPopup, setShowLanguageChangeToHaitianPopup] = useState(false);
    const [hideGettingStarted, setHideGettingStarted] = useState(false);
    const [playAudio, stopAudio] = useAudioPlayer({
        onEnded: () => {
            restartListening();
        }
    });
    const [showFeedbackPopup, setShowFeedbackPopup] = useState(false)
    const isRealtimeHaitian = sourceLanguage.code === "ht-HT" || targetLanguage.code === "ht-HT";

    const [showCountDown, setShowCountDown] = useState(false);
    const handleExceeded = () => { if (!showFeedbackPopup) setShowCountDown(true); };

    useListeningWatchdog({
        listeningStatus,
        onListeningExceeded: handleExceeded,
        onIdleExceeded: handleExceeded
    });
    useWindowClose(()=>{
        endSessionConversationBeacon();
    })

    const {
        initConfigs,
        initAzureSpeech,
        startListening,
        stopListening,
        synthesizeText,
        disposeAzureSpeech,
        updateAuthToken,
        sessionRef
    } = useSpeechEngine({
        sourceLanguage,
        targetLanguage,
        callbacks: {
            onError: (error) => {
                if (error.name === "NotAllowedError") {
                    handleError(new MicrophoneBlockedError(), {
                        showToast: true,
                        toastOptions: {
                            autoClose: false,
                            closeButton: false,
                            closeOnClick: false,
                            position: "bottom-center",
                        }
                    });
                    setListeningStatus(ListeningStatus.Idle);
                } else {
                    handleError(error, {
                        showToast: false
                    });
                    console.log(error);
                }
            },
            onRecognizing: (output) => {
                // REALTIME MODE (Haitian)
                if (isRealtimeHaitian) {
                    setCurrentMessage({
                        sourceText: output.source?.text || "",
                        targetText: output.target?.text || "",
                        sourceLanguageTranscriptionDir: output.source?.language?.transcriptionDir,
                        targetLanguageTranscriptionDir: output.target?.language?.transcriptionDir,
                    });

                    return;
                }

                const spokenLanguage = sourceLanguage.code == output.language ? sourceLanguage : targetLanguage;
                setCurrentMessage({
                    ...output,
                    sourceLanguageTranscriptionDir: spokenLanguage.transcriptionDir,
                });
            },
            onRecognized: async (output, sessionId) => {
                // REALTIME MODE (OpenAI socket)
                if (output.isRealtime) {
                    if (!output.translation?.trim() && !output.transcription?.trim()) return;
                    const spokenLanguage = sourceLanguage;
                    const translatedLanguage = targetLanguage;

                    const translatedLanguagecode = output.target_language;

                    const languageSwapMap = {
                        "ht-HT": "en-US",
                        "en-US": "ht-HT"
                    };

                    const sourceTargetLanguage = languageSwapMap[translatedLanguagecode] || sourceLanguage.code;

                    setConversation((prev) => [
                        ...prev,
                        {
                            sourceLanguage: output.transcription,
                            targetLanguage: output.translation,
                            sourceLanguageLabel: spokenLanguage.langText,
                            targetLanguageLabel: translatedLanguage.langText,
                            sourceLanguageCode: sourceTargetLanguage,
                            targetLanguageCode: translatedLanguagecode,
                            sourceLanguageTranscriptionDir: spokenLanguage.transcriptionDir,
                            targetLanguageTranscriptionDir: translatedLanguage.transcriptionDir,
                        }
                    ]);

                    setCurrentMessage({
                        language: "",
                        text: "",
                    });

                    setListeningStatus(ListeningStatus.DoneProcessing);
                    return;
                }

                // NORMAL (NON-REALTIME) MODE
                stopListening();
                if (!output.text?.trim()) {
                    return;
                }

                setListeningStatus(ListeningStatus.Processing);
                const translateTo = sourceLanguage.code === output.language ? targetLanguage.translationCode || targetLanguage.code : sourceLanguage.code;

                try {
                    const translation = await translationService.translateText({
                        text: output.text,
                        language: [translateTo, output.language],
                        conversation: conversation.filter(x => !x.error).slice(SliceOfConversationToPassToAPI),
                        fallbackTo: translateTo,
                    });

                    if (translation) {
                        const translated_text = translation.translation;
                        const detected_language = translation.detected_language;
                        const corrected_sentence = translation.corrected_sentence;
                        const translated_language = translation.translated_language;

                        const spokenLanguage = sourceLanguage.code.startsWith(detected_language) ? sourceLanguage : targetLanguage;
                        const translatedLanguage = sourceLanguage.code.startsWith(translated_language) ? sourceLanguage : targetLanguage;
                        setConversation((prev) => [
                            ...prev,
                            {
                                sourceLanguage: corrected_sentence,
                                targetLanguage: translated_text,
                                sourceLanguageLabel: spokenLanguage.langText,
                                targetLanguageLabel: translatedLanguage.langText,
                                sourceLanguageCode: spokenLanguage.code,
                                targetLanguageCode: translatedLanguage.code,
                                sourceLanguageTranscriptionDir: spokenLanguage.transcriptionDir,
                                targetLanguageTranscriptionDir: translatedLanguage.transcriptionDir,
                            }
                        ]);

                        appInsights.trackTrace({
                            message: "Language Translation",
                            severityLevel: SeverityLevel.Information,
                            properties: {
                                userSelectedSourceLanguageCode: sourceLanguage.code,
                                userSelectedTargetLanguageCode: targetLanguage.code,
                                recognizedSpeechLanguageCode: spokenLanguage.code,
                                translatedLanguageCode: translatedLanguage.code,
                                spokenText: output.text,
                                correctedText: corrected_sentence,
                                translatedText: translated_text,
                            },
                        });

                        await synthesizeText({ text: translated_text, language: spokenLanguage.code, voiceName: translatedLanguage.dialectVoice, sessionId: sessionId });
                    } else {
                        restartListening();
                    }
                } catch (err) {
                    if (err instanceof TranslationError) {
                        const error = handleError(err, { showToast: false });
                        setConversation((prev) => [
                            ...prev,
                            {
                                sourceLanguage: output.text,
                                errorMessage: error.message,
                            }
                        ]);
                    } else {
                        handleError(err);
                    }
                } finally {
                    setCurrentMessage({
                        language: "",
                        text: "",
                    });
                    setListeningStatus(ListeningStatus.DoneProcessing);
                }
            },
            onSynthesized: async (audioBlob, sessionId) => {
                if ((stopStateRef.current.isStopped && stopStateRef.current.reason === StopReason.NEW_CONVERSATION) ||
                    (sessionId !== sessionRef.current)) {
                    return;
                }
                playAudio(audioBlob);
                setListeningStatus(ListeningStatus.Idle);
            },
            onStartListening: () => {
                setListeningStatus(ListeningStatus.Listening);
            },
            // onStartSynthesizing: () => {
            //     if (isRealtimeHaitian) {
            //         setListeningStatus(ListeningStatus.Idle);
            //         return;
            //     }
            // },
            onStopSynthesizing: () => {
            },
            onStopListening: () => { },
        }
    });

    const setStopState = (isStopped, reason = StopReason.NONE) => {
        stopStateRef.current = {
            isStopped: isStopped,
            reason: reason
        };
    };

    const restartListening = () => {
        if (!stopStateRef.current.isStopped) {
            startListening([sourceLanguage.code, targetLanguage.code]);
            setCurrentMessage({
                language: "",
                text: "",
            });
        }
    };

    useEffect(() => {
        if (sourceLanguage.code && targetLanguage.code) {
             if (targetLanguage.code === "ht-HT") {
                setShowLanguageChangeToHaitianPopup(true);
            }

            if (conversation && conversation.length > 0) {
                setShowLanguageChangePopup(true);
            }

            if (listeningStatus === ListeningStatus.Listening) {
                stopListening();
                setListeningStatus(ListeningStatus.Idle);
            }

            disposeAzureSpeech()
                .then(() => {
                    speechTokenCacheService
                        .fetchToken(updateAuthToken)
                        .then(tokenCache => {
                            initConfigs(tokenCache);
                            initAzureSpeech([sourceLanguage.code, targetLanguage.code]);
                            setListeningStatus(ListeningStatus.Idle);
                            startSessionConversation();
                        })
                        .catch(err => {
                            if (err instanceof SpeechTokenError) {
                                handleError(err, {
                                    showToast: true,
                                    toastOptions: {
                                        autoClose: false,
                                        closeButton: (
                                            <div className="d-flex align-items-center">
                                                <Button onClick={() => window.location.reload()} className="rounded-5">Refresh</Button>
                                            </div>
                                        ),
                                        closeOnClick: false,
                                        position: "bottom-center",
                                    }
                                });
                            } else {
                                handleError(err);
                            }
                        });
                });
        }
    }, [sourceLanguage.code, targetLanguage.code]);

    useEffect(() => {
        return () => {
            disposeAzureSpeech();
        };
    }, []);

    const handleConversation = () => {
        if (!sourceLanguage.code || !targetLanguage.code) {
            toast.error("Please select your preferred language to begin the conversation.");
            return;
        }

        setHideGettingStarted(true);
        stopAudio();
        if (listeningStatus !== ListeningStatus.Listening) {
            setListeningStatus(ListeningStatus.StartingToListen);
            startListening([sourceLanguage.code, targetLanguage.code]);
            setStopState(false);
        } else {
            stopListening();
            setHideGettingStarted(conversation.length > 0);
            setStopState(true, StopReason.CONVERSATION_ENDED);
            setListeningStatus(ListeningStatus.Idle);
        }
    };

    const resetConversation = () => {
        setShowResetPopup(false);
        setHideGettingStarted(false);
        setCurrentMessage({
            language: "",
            text: "",
        });
        setStopState(true, StopReason.NEW_CONVERSATION);
        setListeningStatus(ListeningStatus.Idle);
        setContinuousMode(false);
        stopListening();
        stopAudio();
        setConversation([]);
        disposeAzureSpeech();
        setShowFeedbackPopup(true);
        endSessionConversation();
    };

    const convertToDownloadableArray = (conv) => {
        return conv.map(x => ({
            "Input": x.sourceLanguage,
            "Input Language": x.sourceLanguageLabel,
            "Output": x.targetLanguage,
            "Output Language": x.targetLanguageLabel,
        }));
    }

    const downloadTranscript = () => {
        if (!conversation || conversation.length === 0) return;
        downloadFile(Papa.unparse(convertToDownloadableArray(conversation), { quotes: true }), "file.csv");
    }

    const TranscriptControlPanel = () => (
        <section className={styles['control-transcript-wrapper']}>
            <section className={styles['controls']}>
                <TranscriptControl
                    conversation={conversation}
                    conversationMode={continuousMode}
                    listeningStatus={listeningStatus}
                    onDownloadTranscript={downloadTranscript}
                    onConversationClick={() => setContinuousMode(!continuousMode)}
                    onRecordingClick={handleConversation}
                    onResetClick={() => setShowResetPopup(true)}
                />
            </section>
        </section>
    );

    return (
        <div className={`${styles['app-container']}`}>
            <CountdownModal
                    show={showCountDown}
                    onClose={() => setShowCountDown(false)}
                    endConversation={resetConversation}
            />
            <main className={styles['app-main']}>
                <div className={`${styles['app-content']} container`}>
                    <div className={`${styles["translation-content"]} h-100 w-100 d-flex flex-column justify-content-${continuousMode ? "start" : "around"}`}>
                        <TranscriptionDisplay
                            listeningStatus={listeningStatus}
                            conversation={conversation}
                            currentMessage={currentMessage}
                            conversationMode={continuousMode}
                            hideGettingStarted={hideGettingStarted}
                        />
                    </div>
                    <TranscriptControlPanel />
                </div>
            </main>
            <ModalPopup
                show={showResetPopup}
                heading="End conversation?"
                description="This will clear the saved transcript for the current session."
                buttons={(
                    <>
                        <Button variant="outline-dark" onClick={() => setShowResetPopup(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={resetConversation}>
                            Confirm
                        </Button>
                    </>
                )}
                onClose={() => setShowResetPopup(false)}
            />
            <ModalPopup
                show={showLanguageChangePopup}
                description="Language change will apply to new messages only"
                buttons={(
                    <Button variant="primary" onClick={() => {

                        setShowLanguageChangePopup(false)
                    }}>
                        OK
                    </Button>
                )}
            />

            <ModalPopup
                show={showLanguageChangeToHaitianPopup}
                heading={<div className="d-flex align-items-center gap-2">
                    <IoIosInformationCircle
                        className="text-primary"
                        style={{ fontSize: "2rem" }}
                    />
                    <span className="bold">Note</span>
                </div>}
                description="As Haitian Creole support continues to improve, auto‑translation accuracy and fluency may still vary."
                buttons={(
                    <Button variant="primary" onClick={() => {

                        setShowLanguageChangeToHaitianPopup(false)
                    }}>
                        OK
                    </Button>
                )}
                onClose={() => {
                    setShowLanguageChangeToHaitianPopup(false)
                }}
            />

            {showFeedbackPopup && <Feedback showFeedbackPopup={showFeedbackPopup} setShowFeedbackPopup={setShowFeedbackPopup} conversationId={conversationId} />}
        </div>
    );
};

export default LanguageTranslator;
